import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { PageMetaData } from "@/react/components/Layout/PageMetaData";
import { StandardLayout } from "@/react/components/Layout/StandardLayout";
import { SORT_TYPES } from "@/react/components/Spaces/Header/HeaderNav/SortByList";
import { reactQueryPut } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { useAppendBodyClasses } from "@/react/hooks/useAppendBodyClasses";
import { useIsLoadingAppData } from "@/react/hooks/useIsLoadingAppData";
import { usePostList } from "@circle-react/components/HomePageV2/usePostList";
import { HomeCoverForm } from "@circle-react/components/Layout/StandardLayoutV2/HomeCoverForm";
import { QuickPostBoxModal } from "@circle-react/components/QuickPost/QuickPostBox";
import { useQuickPostV2Modal } from "@circle-react/components/QuickPostV2";
import type { ApiError } from "@circle-react/config/CustomErrors";
import {
  CurrentCommunityContextProvider,
  usePunditUserContext,
} from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { useCanCreatePost } from "@circle-react/hooks/posts/useCanCreatePost";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Loader } from "@circle-react-uikit/Loader";
import { HomePageHeader } from "./Header";
import { HomepageContentWrapper } from "./HomepageContentWrapper";
import { HomepageContextProvider } from "./HomepageContext";
import { PostList } from "./PostList";
import { RightSidebarContent } from "./RightSidebarContent";
import { SuggestedSpacesSection } from "./SuggestedSpacesSection";
import { WelcomeBannerV2 } from "./WelcomeBanner";

const bodyClassNames = "view-homepage";

export const HomePageV2 = () => {
  const { currentCommunity, data, refetch, currentCommunityMember } =
    usePunditUserContext();
  useAppendBodyClasses(bodyClassNames);
  const isLoadingAppData = useIsLoadingAppData();
  const { canCreatePost: shouldDisplayQuickPostBox } = useCanCreatePost();

  const postModal = useQuickPostV2Modal();

  const memberDefaultSort = currentCommunityMember
    ? currentCommunityMember.home_page_posts_sort ||
      currentCommunity?.default_post_sort_logged_in_user
    : currentCommunity?.default_post_sort_logged_out_user;

  const defaultSort = memberDefaultSort || SORT_TYPES[0];

  const { isLoading: isLoadingPosts, posts } = usePostList(defaultSort);

  const mutation = useMutation<any, ApiError, any>(
    data =>
      reactQueryPut(
        internalApi.communityMembers.preference(currentCommunityMember),
        data,
      ),
    {
      onSuccess: refetch,
    },
  );

  const updateSortPreference = (value: any) => {
    if (currentCommunityMember) {
      mutation.mutate({ home_page_posts_sort: value });
    }
  };

  const isAdmin = isCommunityAdmin(currentCommunityMember);

  if (isLoadingPosts || isLoadingAppData) {
    return <Loader />;
  }

  if (!isLoadingPosts && !posts.length && isAdmin) {
    return (
      <>
        <HomePageHeader
          updateSortPreference={updateSortPreference}
          defaultSort={defaultSort}
        />
        <EmptyState
          header={t("home_page.no_posts.title")}
          description={t("home_page.no_posts.description")}
          primaryLabel={t("home_page.no_posts.cta")}
          onPrimaryClick={async () => {
            await postModal.show();
          }}
        />
      </>
    );
  }

  return (
    <HomepageContextProvider>
      <CurrentCommunityContextProvider>
        <HomeCoverForm />
        <StandardLayout.Body>
          <PageMetaData title={t("feed")} />
          <HomepageContentWrapper>
            {!!currentCommunity && (
              <>
                <div className="w-full">
                  {!isLoadingAppData && (
                    <WelcomeBannerV2
                      community_member_id={currentCommunity.id}
                    />
                  )}
                  {shouldDisplayQuickPostBox && (
                    <div className="mb-6 hidden md:block">
                      <QuickPostBoxModal />
                    </div>
                  )}
                  {!!data?.home_page_posts_view && (
                    <>
                      <HomePageHeader
                        updateSortPreference={updateSortPreference}
                        defaultSort={defaultSort}
                      />
                      <PostList defaultSort={defaultSort} />
                    </>
                  )}
                  {!data?.home_page_posts_view && !isLoadingAppData && (
                    <SuggestedSpacesSection />
                  )}
                </div>
                <RightSidebarContent />
              </>
            )}
          </HomepageContentWrapper>
        </StandardLayout.Body>
      </CurrentCommunityContextProvider>
    </HomepageContextProvider>
  );
};

export default HomePageV2;
